// Spacing constants (in px, matching iOS pt values)
export const AppSpacing = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
} as const;

// Border radius constants
export const AppRadius = {
  sm: 6,
  md: 12,
  lg: 16,
} as const;

// Theme colors — 10 options matching iOS AppThemeStyle
export const ThemeColors = {
  mint: { name: "Mint", value: "#00C7BE" },
  blue: { name: "Blue", value: "#007AFF" },
  purple: { name: "Purple", value: "#AF52DE" },
  pink: { name: "Pink", value: "#FF2D55" },
  orange: { name: "Orange", value: "#FF9500" },
  teal: { name: "Teal", value: "#30B0C7" },
  indigo: { name: "Indigo", value: "#5856D6" },
  red: { name: "Red", value: "#FF3B30" },
  yellow: { name: "Yellow", value: "#FFCC00" },
  green: { name: "Green", value: "#34C759" },
} as const;

export type ThemeColorKey = keyof typeof ThemeColors;

// Font families — 8 options matching iOS AppFontFamily
export const FontFamilies = {
  system: { name: "System", value: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" },
  serif: { name: "Serif", value: "Georgia, 'Times New Roman', serif" },
  georgia: { name: "Georgia", value: "Georgia, serif" },
  avenirNext: { name: "Avenir Next", value: "'Avenir Next', 'Avenir', sans-serif" },
  futura: { name: "Futura", value: "Futura, 'Trebuchet MS', sans-serif" },
  gillSans: { name: "Gill Sans", value: "'Gill Sans', 'Gill Sans MT', sans-serif" },
  chalkboard: { name: "Chalkboard", value: "'Chalkboard SE', 'Comic Sans MS', cursive" },
  markerFelt: { name: "Marker Felt", value: "'Marker Felt', 'Comic Sans MS', cursive" },
} as const;

export type FontFamilyKey = keyof typeof FontFamilies;

// Typography scale (matching iOS AppFont sizes)
export const FontSizes = {
  pageTitle: "2.125rem",     // 34px — largeTitle
  sectionTitle: "1.0625rem", // 17px — headline, bold
  itemTitle: "0.9375rem",    // 15px — subheadline, bold
  body: "1.0625rem",         // 17px — body
  subtitle: "0.9375rem",     // 15px — subheadline
  meta: "0.75rem",           // 12px — caption
  microMeta: "0.6875rem",    // 11px — caption2
} as const;

// Grocery categories matching iOS GroceryCategoryEnum
export const GroceryCategories = [
  "Produce",
  "Dairy",
  "Meat & Seafood",
  "Bakery",
  "Frozen",
  "Canned & Jarred",
  "Snacks",
  "Beverages",
  "Condiments & Sauces",
  "Grains & Pasta",
  "Household",
  "Personal Care",
  "Other",
] as const;

export const DEFAULT_THEME: ThemeColorKey = "mint";
export const DEFAULT_FONT: FontFamilyKey = "system";

export const DIETARY_RESTRICTIONS = [
  'Vegetarian', 'Vegan', 'Pescatarian', 'Halal',
  'Kosher', 'Gluten-Free', 'Lactose-Free', 'Keto',
] as const;

// Dashboard sections — order matches iOS default
export const DASHBOARD_SECTIONS = [
  { id: 'today', label: 'Today', icon: '\u{1F4C5}' },
  { id: 'meals', label: 'Meals', icon: '\u{1F37D}\uFE0F' },
  { id: 'responsibilities', label: 'Responsibilities', icon: '\u2705' },
  { id: 'chores', label: 'My Chores', icon: '\u{1F9F9}' },
  { id: 'lists', label: 'Lists', icon: '\u{1F4DD}' },
  { id: 'weekend', label: 'Weekend Plans', icon: '\u{1F324}\uFE0F' },
  { id: 'family', label: 'Family Updates', icon: '\u{1F468}\u200D\u{1F469}\u200D\u{1F467}\u200D\u{1F466}' },
] as const;

export type DashboardSectionId = typeof DASHBOARD_SECTIONS[number]['id'];

// Legal entity + product attribution. Single source for the auth-screen
// footer and the in-app About panel so the strings can't drift.
export const APP_NAME = "Zoey Organizer";
export const LEGAL_NAME = "BuiltByDad, LLC";
export const COPYRIGHT_START_YEAR = 2026;
export const APP_VERSION = "0.1.0";

// Live store listings for the native apps. Single source for any
// "download the app" affordance on the web (e.g. the auth-screen banner).
export const APP_STORE_URL =
  "https://apps.apple.com/us/app/zoey-organizer/id6769154812";
export const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.builtbydad.zoey";

export function copyrightYears(now: Date = new Date()): string {
  const year = now.getFullYear();
  return year > COPYRIGHT_START_YEAR ? `${COPYRIGHT_START_YEAR}–${year}` : `${COPYRIGHT_START_YEAR}`;
}

export function copyrightLine(formal: boolean, now: Date = new Date()): string {
  const years = copyrightYears(now);
  return formal
    ? `Copyright © ${years} ${LEGAL_NAME}. All rights reserved.`
    : `© ${years} ${LEGAL_NAME}`;
}
